import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { preferencesApi } from '@/api/endpoints/preferences';
import { analytics } from '@/lib/analytics';
import toast from 'react-hot-toast';

const FAVORITES_KEY = 'ml4-favorites';
const HIDDEN_KEY = 'ml4-hidden';

interface UserPreferencesContextValue {
  favorites: Set<string>;
  hidden: Set<string>;
  isFavorite: (barId: string) => boolean;
  isHidden: (barId: string) => boolean;
  toggleFavorite: (barId: string) => void;
  toggleHidden: (barId: string) => void;
  isLoading: boolean;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

function loadFromStorage(key: string): Set<string> {
  try {
    const stored = localStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function saveToStorage(key: string, set: Set<string>) {
  localStorage.setItem(key, JSON.stringify([...set]));
}

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState<Set<string>>(() => loadFromStorage(FAVORITES_KEY));
  const [hidden, setHidden] = useState<Set<string>>(() => loadFromStorage(HIDDEN_KEY));
  const [isLoading, setIsLoading] = useState(false);
  const hasSynced = useRef(false);
  const prevAuthenticated = useRef(isAuthenticated);

  // Sync with server on login
  useEffect(() => {
    // Only sync when transitioning from not authenticated to authenticated
    if (isAuthenticated && !prevAuthenticated.current && !hasSynced.current) {
      hasSynced.current = true;
      setIsLoading(true);

      const localFavorites = [...loadFromStorage(FAVORITES_KEY)];
      const localHidden = [...loadFromStorage(HIDDEN_KEY)];

      preferencesApi
        .sync({ favoriteBarIds: localFavorites, hiddenBarIds: localHidden })
        .then(({ preferences }) => {
          const newFavorites = new Set(preferences.favoriteBarIds);
          const newHidden = new Set(preferences.hiddenBarIds);
          setFavorites(newFavorites);
          setHidden(newHidden);
          saveToStorage(FAVORITES_KEY, newFavorites);
          saveToStorage(HIDDEN_KEY, newHidden);
        })
        .catch(() => {
          // Silently fail - keep local state
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    // Reset sync flag on logout
    if (!isAuthenticated && prevAuthenticated.current) {
      hasSynced.current = false;
    }

    prevAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  const isFavorite = useCallback((barId: string) => favorites.has(barId), [favorites]);
  const isHidden = useCallback((barId: string) => hidden.has(barId), [hidden]);

  const toggleFavorite = useCallback(
    (barId: string) => {
      const isCurrentlyFavorite = favorites.has(barId);

      // Optimistic update
      const newFavorites = new Set(favorites);
      const newHidden = new Set(hidden);

      if (isCurrentlyFavorite) {
        newFavorites.delete(barId);
      } else {
        newFavorites.add(barId);
        // Remove from hidden (mutual exclusivity)
        newHidden.delete(barId);
      }

      setFavorites(newFavorites);
      setHidden(newHidden);
      saveToStorage(FAVORITES_KEY, newFavorites);
      saveToStorage(HIDDEN_KEY, newHidden);

      // Track analytics
      if (isCurrentlyFavorite) {
        analytics.removeFavorite(barId);
      } else {
        analytics.addFavorite(barId);
      }

      // Sync with server if authenticated
      if (isAuthenticated) {
        const apiCall = isCurrentlyFavorite
          ? preferencesApi.removeFavorite(barId)
          : preferencesApi.addFavorite(barId);

        apiCall.catch(() => {
          // Revert on error
          setFavorites(favorites);
          setHidden(hidden);
          saveToStorage(FAVORITES_KEY, favorites);
          saveToStorage(HIDDEN_KEY, hidden);
          toast.error('Failed to update favorites');
        });
      }
    },
    [favorites, hidden, isAuthenticated]
  );

  const toggleHidden = useCallback(
    (barId: string) => {
      const isCurrentlyHidden = hidden.has(barId);

      // Optimistic update
      const newHidden = new Set(hidden);
      const newFavorites = new Set(favorites);

      if (isCurrentlyHidden) {
        newHidden.delete(barId);
      } else {
        newHidden.add(barId);
        // Remove from favorites (mutual exclusivity)
        newFavorites.delete(barId);
      }

      setHidden(newHidden);
      setFavorites(newFavorites);
      saveToStorage(HIDDEN_KEY, newHidden);
      saveToStorage(FAVORITES_KEY, newFavorites);

      // Sync with server if authenticated
      if (isAuthenticated) {
        const apiCall = isCurrentlyHidden
          ? preferencesApi.removeHidden(barId)
          : preferencesApi.addHidden(barId);

        apiCall.catch(() => {
          // Revert on error
          setHidden(hidden);
          setFavorites(favorites);
          saveToStorage(HIDDEN_KEY, hidden);
          saveToStorage(FAVORITES_KEY, favorites);
          toast.error('Failed to update hidden bars');
        });
      }
    },
    [favorites, hidden, isAuthenticated]
  );

  return (
    <UserPreferencesContext.Provider
      value={{
        favorites,
        hidden,
        isFavorite,
        isHidden,
        toggleFavorite,
        toggleHidden,
        isLoading,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
